import logging from 'utils/logging';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUNDSTATE_ROUND_COMPLETE,
  GAMESTATE_ROUND_FINAL
} from '../../gamestate/constants';
import { MOMENT_TYPES, NARRATION_KEYS, HOST_STATUS } from '../hostStateSlice';
import { advanceState } from '../../gamestate/GameState';
import { determineNextRound } from '../../gamestate/roundCompletionUtils';
import Provider from '../../../provider';
import { PLAYER_STATES, getInterRoundPhase } from '../../player/playerConstants';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.RoundComplete');
log.setLevel('info');

// Track which rounds have generated wrap-up narration to avoid duplicates
const roundsGenerated = new Set();

export const resetRoundCompleteCache = () => {
  roundsGenerated.clear();
};
registerCacheReset('round', resetRoundCompleteCache);

/**
 * Moment that triggers wrap-up narration generation when the last question of a round is in RESULTS state
 *
 * IMPORTANT: This moment triggers EARLY (during QUESTION_RESULTS state) when all questions
 * in the round are complete. This allows us to PRE-GENERATE the round wrap-up narration
 * while the judging results narration is playing. When the judging results narration
 * completes and advances the state to ROUND_COMPLETE, the wrap-up narration will already
 * be ready and can play immediately without delay.
 *
 * Flow:
 * 1. Last question results shown → shouldTrigger fires (pre-generate wrap-up narration)
 * 2. Judging results narration plays in QUESTION_RESULTS state
 * 3. Judging narration completes, state advances to ROUND_COMPLETE
 * 4. Wrap-up narration plays immediately (already generated!)
 */
export const RoundCompleteMoment = {
  id: 'ROUND_COMPLETE',
  shouldTrigger: ({ prevState, currentState, currentPlayers, questions, gameObject }) => {
    // Only trigger in ROUND_COMPLETE state (after scores have been committed)
    if (currentState?.currentState !== GAMESTATE_ROUND) {
      return false;
    }

    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_ROUND_COMPLETE) {
      return false;
    }

    // Skip for final round (has different flow)
    if (currentState?.round === GAMESTATE_ROUND_FINAL) {
      return false;
    }

    if (!gameObject || !currentState.round) {
      return false;
    }

    // Check if we've already generated wrap-up for this round
    const roundKey = `${gameObject.id}_${currentState.round}`;
    if (roundsGenerated.has(roundKey)) {
      log.debug(`Round wrap-up narration already generated for ${roundKey}`);
      return false;
    }

    // Mark as generated to prevent duplicates
    roundsGenerated.add(roundKey);

    log.info(`Pre-generating round wrap-up narration for ${currentState.round} (all questions complete)`);

    // Determine next round dynamically based on available categories
    const nextRound = determineNextRound(currentState.round, gameObject?.categories);

    // Get current player standings with scores
    const currentStandings = (currentPlayers || [])
      .map(player => ({
        id: player.id,
        name: player.name,
        score: player.score || 0
      }))
      .sort((a, b) => b.score - a.score); // Sort by score descending

    // Get score changes during this round (if available)
    // roundStartScores is structured as: { practice: { player1: 0 }, round1: { player1: 50 }, ... }
    const roundStartScores = gameObject?.state?.roundStartScores?.[currentState.round];
    let scoreChanges = null;

    if (roundStartScores) {
      scoreChanges = currentStandings.map(player => {
        const initialScore = roundStartScores[player.id] || 0;
        const change = player.score - initialScore;
        return {
          id: player.id,
          name: player.name,
          initialScore,
          currentScore: player.score,
          change
        };
      }).sort((a, b) => b.change - a.change); // Sort by score change descending
    }

    // Create narration params
    const narrationKey = `${NARRATION_KEYS.ROUND_COMPLETE}_${currentState.round}`;

    return {
      shouldTriggerAction: false, // Don't advance state - that happens after judging narration plays
      shouldGenerateNarration: true, // Pre-generate the narration now!
      narrationParams: {
        narrationKey,
        momentType: MOMENT_TYPES.ROUND_COMPLETE,
        context: {
          currentRound: currentState.round,
          nextRound,
          standings: currentStandings,
          scoreChanges, // May be null for first round or if not available
          playerCount: currentPlayers?.length || 0
        }
      }
    };
  },

  /**
   * Trigger action (not used - state advancement happens via JudgingComplete onComplete callback)
   */
  triggerAction: async (gameObject, players, dispatch) => {
    log.debug('RoundComplete triggerAction called (no action needed)');
  }
};

/**
 * Playback moment for round wrap-up narration
 *
 * This plays when we're in ROUND_COMPLETE state. By this time, the narration should
 * already be generated (pre-generated during QUESTION_RESULTS), so it plays immediately.
 */
export const RoundCompletePlaybackMoment = {
  id: 'ROUND_COMPLETE_PLAYBACK',
  momentType: MOMENT_TYPES.ROUND_COMPLETE,
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    // Check if narration exists
    if (!narration) {
      return false;
    }

    // Check if this is a round complete narration
    if (!narration.momentType || narration.momentType !== MOMENT_TYPES.ROUND_COMPLETE) {
      return false;
    }

    // Check if we're in the ROUND_COMPLETE state
    const inRoundCompleteState = gameState?.currentState === GAMESTATE_ROUND &&
                                  gameState?.roundState === GAMESTATE_ROUNDSTATE_ROUND_COMPLETE;
    if (!inRoundCompleteState) {
      return false;
    }

    // Check if narration is ready to play
    const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
    if (!narrationReady) {
      return false;
    }

    // Check if host is available to speak
    const hostAvailable = hostState?.status !== HOST_STATUS.SPEAKING;
    if (!hostAvailable) {
      return false;
    }

    // Check if this specific narration has been played before
    const notPlayedYet = !playedNarrations.has(narration.key);
    if (!notPlayedYet) {
      return false;
    }

    log.debug(`ROUND_COMPLETE_PLAYBACK shouldPlay: narrationReady=${narrationReady}, inRoundCompleteState=${inRoundCompleteState}, hostAvailable=${hostAvailable}, notPlayedYet=${notPlayedYet}`);

    return true;
  },

  /**
   * Callback when the round wrap-up narration is complete
   * Resets all player states to INTER_ROUND.WAITING and advances to LOBBY for inter-round ready-up
   */
  onComplete: async (gameId, dispatch, getState) => {
    log.info('Round wrap-up narration complete, resetting player states and advancing to inter-round lobby');

    try {
      const state = getState();
      const gameObject = state.game?.game;

      if (!gameObject) {
        log.error('Game object not found in state');
        return;
      }

      // Get the current players
      const players = Object.values(state.player?.entities || {});

      // Reset all player states to INTER_ROUND.WAITING for the inter-round lobby
      const round = gameObject.state.round;
      const phase = getInterRoundPhase(round);
      log.info(`Resetting ${players.length} players to INTER_ROUND.WAITING state (phase: ${phase})`);
      await Promise.all(
        players.map(player =>
          Provider.updatePlayerState(
            gameId,
            player.id,
            phase,
            { state: PLAYER_STATES.INTER_ROUND.WAITING }
          )
        )
      );

      // Advance to LOBBY for inter-round ready-up
      // The state machine will handle the transition from ROUND_COMPLETE to LOBBY
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing game state after round wrap-up narration:', error);
    }
  }
};

const api = {
  trigger: RoundCompleteMoment,
  playback: RoundCompletePlaybackMoment
};

export default api;
